import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

// Import UI components
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/hooks/use-toast";
import {
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

// Import select components
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from "@/components/ui/select";

// Import API
import { api } from "@/lib/api";

// Import types
import {
  VerifiedPhoneNumber,
  StrategyWithVersions,
  CallCreateRequest,
} from "@/lib/types";

// Define the schema for form validation
const CreateCallFormSchema = z.object({
  callee_phone_number: z.string().min(1, "Callee phone number is required"),
  verified_phone_number_id: z
    .string()
    .min(1, "Please select a verified phone number"),
  strategy_id: z.string().min(1, "Please select a strategy"),
  strategy_version_id: z.string().min(1, "Please select a strategy version"),
  call_specific_info: z.string().optional(),
});

type CreateCallFormValues = z.infer<typeof CreateCallFormSchema>;

function CreateCallForm({
  organizationId,
  onSuccess,
}: {
  organizationId: string;
  onSuccess?: () => void;
}) {
  const [verifiedPhoneNumbers, setVerifiedPhoneNumbers] = useState<
    VerifiedPhoneNumber[]
  >([]);
  const [strategies, setStrategies] = useState<StrategyWithVersions[]>([]);

  // Load verified phone numbers and strategies when component mounts
  useEffect(() => {
    // Fetch verified phone numbers
    api
      .getVerifiedPhoneNumbers(organizationId)
      .then(setVerifiedPhoneNumbers)
      .catch((error) => {
        console.error("Failed to fetch verified phone numbers:", error);
        toast({
          title: "Failed to load verified phone numbers",
          description: String(error),
          variant: "destructive",
        });
      });

    // Fetch strategies
    api
      .getOrganizationStrategies(organizationId)
      .then((strategies) => {
        // Sort strategies by most recently modified first
        const sortedStrategies = strategies.sort((a, b) => {
          const aDate = new Date(a.updated_at || a.created_at);
          const bDate = new Date(b.updated_at || b.created_at);
          return bDate.getTime() - aDate.getTime();
        });

        // Also sort the versions within each strategy
        sortedStrategies.forEach((strategy) => {
          if (strategy.versions) {
            strategy.versions.sort((a, b) => {
              const aDate = new Date(a.updated_at || a.created_at);
              const bDate = new Date(b.updated_at || b.created_at);
              return bDate.getTime() - aDate.getTime();
            });
          }
        });

        setStrategies(sortedStrategies);
      })
      .catch((error) => {
        console.error("Failed to fetch strategies:", error);
        toast({
          title: "Failed to load strategies",
          description: String(error),
          variant: "destructive",
        });
      });
  }, [organizationId]);

  const form = useForm<CreateCallFormValues>({
    resolver: zodResolver(CreateCallFormSchema),
    defaultValues: {
      callee_phone_number: "",
      verified_phone_number_id: "",
      strategy_id: "",
      strategy_version_id: "",
      call_specific_info: "",
    },
  });

  // Handle strategy selection change to reset strategy version
  const watchStrategyId = form.watch("strategy_id");
  useEffect(() => {
    // When strategy changes, reset strategy_version_id
    form.setValue("strategy_version_id", "");
  }, [watchStrategyId]);

  const onSubmit = async (data: CreateCallFormValues) => {
    try {
      const callSpecificInfo = data.call_specific_info
        ? { "Call Specific Info": data.call_specific_info }
        : {};

      const callCreateRequest: CallCreateRequest = {
        callee_phone_number: data.callee_phone_number,
        strategy_version_id: data.strategy_version_id,
        verified_phone_number_id: data.verified_phone_number_id,
        call_specific_info: callSpecificInfo,
      };

      const newCall = await api.createCall(callCreateRequest);

      toast({ title: "Call created successfully" });
      form.reset();
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error("Failed to create call:", error);
      toast({
        title: "Failed to create call",
        description: String(error),
        variant: "destructive",
      });
    }
  };

  return (
    <div>
      <DialogHeader className="mb-6">
        <DialogTitle>Create Call</DialogTitle>
        <DialogDescription>
          Fill in the details to create a new call.
        </DialogDescription>
      </DialogHeader>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <FormItem>
            <FormLabel>Callee Phone Number</FormLabel>
            <FormControl>
              <Input
                placeholder="+1234567890"
                {...form.register("callee_phone_number")}
              />
            </FormControl>
            <FormMessage />
          </FormItem>

          <FormItem>
            <FormLabel>Verified Phone Number</FormLabel>
            <FormControl>
              <Select
                value={form.watch("verified_phone_number_id")}
                onValueChange={(value) => {
                  form.setValue("verified_phone_number_id", value);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a verified phone number" />
                </SelectTrigger>
                <SelectContent>
                  {verifiedPhoneNumbers.length === 0 ? (
                    <div className="text-gray-500 text-sm p-3">
                      No verified phone numbers available. Please verify a phone
                      number.
                    </div>
                  ) : (
                    verifiedPhoneNumbers.map((phoneNumber) => (
                      <SelectItem key={phoneNumber.id} value={phoneNumber.id}>
                        {phoneNumber.phone_number}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </FormControl>
            <FormMessage />
          </FormItem>

          <FormItem>
            <FormLabel>Strategy</FormLabel>
            <FormControl>
              <Select
                value={form.watch("strategy_id")}
                onValueChange={(value) => {
                  form.setValue("strategy_id", value);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a strategy" />
                </SelectTrigger>
                <SelectContent>
                  {strategies.length === 0 ? (
                    <div className="text-gray-500 text-sm p-3">
                      No strategies available. Please add a strategy.
                    </div>
                  ) : (
                    strategies.map((strategy) => (
                      <SelectItem key={strategy.id} value={strategy.id}>
                        {strategy.name}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </FormControl>
            <FormMessage />
          </FormItem>

          {form.watch("strategy_id") && (
            <FormItem>
              <FormLabel>Strategy Version</FormLabel>
              <FormControl>
                <Select
                  value={form.watch("strategy_version_id")}
                  onValueChange={(value) => {
                    form.setValue("strategy_version_id", value);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a strategy version" />
                  </SelectTrigger>
                  <SelectContent>
                    {strategies
                      .find((s) => s.id === form.watch("strategy_id"))
                      ?.versions.map((version) => (
                        <SelectItem key={version.id} value={version.id}>
                          {version.version_description ||
                            `Version ${version.id}`}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}

          <FormItem>
            <FormLabel>Call Specific Info (Optional)</FormLabel>
            <FormControl>
              <Textarea
                placeholder="Enter call specific information here..."
                {...form.register("call_specific_info")}
              />
            </FormControl>
            <FormMessage />
          </FormItem>

          <DialogFooter>
            <Button type="submit">Create Call</Button>
          </DialogFooter>
        </form>
      </Form>
    </div>
  );
}

export default CreateCallForm;
